import { useEffect, useState } from 'react';

const useInsightsData = ({
  insightsType,
  viewMode,
  getDateRange,
  dateRangeParams,
  selectedClinicId,
  fetchInsights,
  fetchTasksSnapshots,
  fetchConsultationsSnapshots,
  fetchDocumentReviewInsights,
  fetchDocumentsReviewAggregate,
  fetchConsultationsReviewSummary,
  fetchConsultationsReviewAggregate,
}) => {
  const [data, setData] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [staffAggregateData, setStaffAggregateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskData, setTaskData] = useState([]);
  const [consultationData, setConsultationData] = useState([]);
  const [consultationStaffAggregateData, setConsultationStaffAggregateData] = useState([]);

  useEffect(() => {
    const fetchInsightsData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { from, to } = getDateRange();

        if (insightsType === 'documents') {
          const promises = [
            new Promise((resolve, reject) => {
              fetchInsights(
                from,
                to,
                selectedClinicId,
                resolve,
                reject
              );
            }),
            new Promise((resolve, reject) => {
              fetchDocumentReviewInsights(
                from,
                to,
                selectedClinicId,
                resolve,
                reject
              );
            })
          ];

          if (viewMode === 'staff' && fetchDocumentsReviewAggregate) {
            promises.push(
              new Promise((resolve, reject) => {
                fetchDocumentsReviewAggregate(
                  from,
                  to,
                  selectedClinicId,
                  resolve,
                  reject
                );
              })
            );
          }

          const results = await Promise.all(promises);
          const [insights, reviewInsights, staffAggregate] = results;

          const transformedData = insights.map(snapshot => {
            return {
              ...snapshot,
              storageSnapshot: {
                ...snapshot.storageSnapshot,
                documentsUnassigned: snapshot.storageSnapshot.documentsReadyForReview - snapshot.storageSnapshot.documentsAssigned,
              }
            };
          });

          setData(transformedData);
          setConsultationData([]);
          setReviewData(reviewInsights);
          setConsultationStaffAggregateData([]);

          if (viewMode === 'staff' && staffAggregate) {
            setStaffAggregateData(staffAggregate);
          }
        } else if (insightsType === 'tasks' && fetchTasksSnapshots) {
          const taskSnapshots = await new Promise((resolve, reject) => {
            fetchTasksSnapshots(
              from,
              to,
              selectedClinicId,
              resolve,
              reject
            );
          });

          const transformedTaskData = taskSnapshots.map(snapshot => {
            return {
              ...snapshot,
              stateSnapshot: {
                ...snapshot.stateSnapshot,
                tasksUnassigned: snapshot.stateSnapshot.incompleteTasks - snapshot.stateSnapshot.tasksAssigned,
              }
            };
          });

          setTaskData(transformedTaskData);
          setConsultationData([]);
          setReviewData(null);
          setStaffAggregateData([]);
          setConsultationStaffAggregateData([]);
        } else if (insightsType === 'consultations' && fetchConsultationsSnapshots) {
          const promises = [
            new Promise((resolve, reject) => {
              fetchConsultationsSnapshots(
                from,
                to,
                selectedClinicId,
                resolve,
                reject
              );
            })
          ];

          if (fetchConsultationsReviewSummary) {
            promises.push(
              new Promise((resolve, reject) => {
                fetchConsultationsReviewSummary(
                  from,
                  to,
                  selectedClinicId,
                  resolve,
                  reject
                );
              })
            );
          }

          if (viewMode === 'staff' && fetchConsultationsReviewAggregate) {
            promises.push(
              new Promise((resolve, reject) => {
                fetchConsultationsReviewAggregate(
                  from,
                  to,
                  selectedClinicId,
                  resolve,
                  reject
                );
              })
            );
          }

          const results = await Promise.all(promises);
          const [consultationSnapshots, consultationReviewInsights, consultationAggregate] = results;

          setConsultationData(consultationSnapshots || []);
          setData([]);
          setTaskData([]);
          setReviewData(consultationReviewInsights || null);
          setStaffAggregateData([]);
          setConsultationStaffAggregateData(
            viewMode === 'staff' && consultationAggregate ? consultationAggregate : []
          );
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in fetchInsightsData:', err);
        setError(err.message || 'Failed to fetch insights data');
        setLoading(false);
      }
    };

    fetchInsightsData();
  }, [fetchInsights, fetchTasksSnapshots, fetchConsultationsSnapshots, fetchDocumentReviewInsights, fetchDocumentsReviewAggregate, fetchConsultationsReviewSummary, fetchConsultationsReviewAggregate, dateRangeParams, selectedClinicId, getDateRange, viewMode, insightsType]);

  return {
    data,
    reviewData,
    staffAggregateData,
    loading,
    error,
    taskData,
    consultationData,
    consultationStaffAggregateData,
  };
};

export default useInsightsData;
